import _ from "lodash";
import React from "react";
import { graphql, compose } from "lib/apolloClient";
import { withRouter } from "react-router";
import { connect } from "react-redux";
import {
  getUserInfo,
  getUserIdOnPortalType,
} from "Login/modules/LoginModule/LoginUtils";
import {
  getAcademicYearId,
  getCurrentCurriculumContextForJumpTo,
  getIsStaff,
} from "modules/CommonSelectors";
import { getProjectGroupsFromCache } from "GlobalHeader/modules/GraphqlHelpers";
import { getUserCoursesV2CountQuery } from "Courses/modules/CourseQueries";
import { getUserCoursesV2CountFromCache } from "Courses/modules/CoursesGraphqlHelpers";
import {
  toggleGlobalSearchBarModal,
  setActiveFeatureWithKeyboardShortcuts,
} from "modules/Services";
import JumpToFeature from "GlobalHeader/components/JumpToFeature";
import JumpToSkeletonLoader from "./JumpToSkeletonLoader";
import classes from "./JumpToSection.scss";
import { getUserProjectGroupsFromCache } from "Teacher/routes/Projects/modules/ProjectGraphqlHelpers";
import {
  getUserProjectGroupsQuery,
  getProjectGroupCategoriesQuery,
} from "Teacher/routes/Projects/modules/ProjectQueries";
import { getUserIdAndEntityTypeByUserMemoize } from "Teacher/modules/TeacherModule";
import ACLStore from "lib/aclStore";
import {
  PROJECT_SUBTYPE_CORE,
  PROJECT_SUBTYPE_SUBJECT_GROUP_PROJECT,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { getConfigValue } from "AppComponents/GlobalSearchBar/components/Utils";

import { getHomeroomStudentsCountQuery } from "Homeroom/modules/HomeroomQueries";
import { getHomeroomStudentsCountFromCache } from "Homeroom/modules/HomeroomGraphqlHelpers";

import { selectedAcademicYearSelector } from "Platform/modules/PlatformModule";

import getEvaluationCyclesGraphql from "SnP_v2/modules/EnhanceQueries/getEvaluationCyclesGraphql";
import { getProjectGroupCategoriesVariablesMemoized } from "Projects/modules/Utils";
import withControlParameters from "lib/aclService/hoc/withControlParameters";
import { getRnPQueryVariablesForProjectGroupCategories } from "./JumpToSectionUtils";
import {
  getLearningCoursesCountQuery,
  getClassesCountQuery,
} from "LearningCourses/modules/LearningCoursesQueries";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { isBlueprintTeacherCourseAssociationEnabled } from "LearningCourse/modules/LearningCourseUtils";

const JumpToSection = props => {
  const {
    setActiveFeatureWithKeyboardShortcuts,
    userCoursesCount,
    userLearningCoursesCount,
    projectGroups,
    cachedProjectGroups,
    isGlobalSearchJumpToRevampEnabled,
    showSearchBar,
    toggleGlobalSearchBarModal,
    updateAvailableShortcutsMapping,
    homeroomStudentCount,
    totalNewSnpCycles,
    isData,
    isLoading,
  } = props;

  const closeGlobalSearchAndJumpToModal = () => {
    if (showSearchBar) {
      toggleGlobalSearchBarModal();
    }
    setActiveFeatureWithKeyboardShortcuts(null);
  };

  return (
    <div className={classes.container}>
      {isLoading && !isData ? (
        <JumpToSkeletonLoader />
      ) : (
        <JumpToFeature
          userCoursesCount={userCoursesCount}
          userLearningCoursesCount={userLearningCoursesCount}
          projectGroups={
            isGlobalSearchJumpToRevampEnabled
              ? projectGroups
              : cachedProjectGroups
          }
          onSelectFeatureCallback={closeGlobalSearchAndJumpToModal}
          updateAvailableShortcutsMapping={updateAvailableShortcutsMapping}
          homeroomStudentCount={homeroomStudentCount}
          totalNewSnpCycles={totalNewSnpCycles}
        />
      )}
    </div>
  );
};

const getIsGlobalSearchJumpToRevampEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableGlobalSearchJumpToRevamp");
};

const getYearGroupCurriculumProgramFilterMemoized = _.memoize(
  curriculumProgramIds => {
    return !ACLStore.can("FeatureFlag:ShowAllYearGroupProjectGroups")
      ? {
          yearGroupCurriculumProgramIds: curriculumProgramIds,
        }
      : {};
  },
  JSON.stringify
);

const BLANK_ARRAY = [];

const staffProjectGroupSubTypeFilters = [
  PROJECT_SUBTYPE_CORE,
  PROJECT_SUBTYPE_SUBJECT_GROUP_PROJECT,
];

const _getHomeroomStudentsCountFilters = ({
  userId,
  academicYearSelected,
  currentCurriculumProgramId,
}) => {
  return {
    id: userId,
    staffStudentsFilters: {
      relation: "HOMEROOM",
      academicYearIds: _.get(academicYearSelected, "id", null),
      curriculumProgramIds: currentCurriculumProgramId,
    },
  };
};

const getHomeroomStudentsCountFilters = _.memoize(
  _getHomeroomStudentsCountFilters,
  JSON.stringify
);

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const user_type = userInfo.user_type;
  const identityId = userInfo.identityId;
  const role = userInfo.role;
  const portalType = "PLANNER";
  const { userEntityType } = getUserIdAndEntityTypeByUserMemoize({
    userInfo,
  });

  const userId =
    user_type === "parent"
      ? userInfo.childID
      : getUserIdOnPortalType({ state, portalType });

  const {
    id: currentCurriculumProgramId,
    type: currentCurriculumProgramType,
    nodeInterfaceType: curriculumNodeInterfaceType,
  } = getCurrentCurriculumContextForJumpTo(state);

  const currentAcademicYearId = getAcademicYearId(state);

  const curriculumProgramIds = [currentCurriculumProgramId];

  const yearGroupCurriculumProgramFilter = getYearGroupCurriculumProgramFilterMemoized(
    curriculumProgramIds
  );

  const isStaff = getIsStaff(state);
  const projectGroupSubTypeFilters = isStaff
    ? staffProjectGroupSubTypeFilters
    : BLANK_ARRAY;

  const showSearchBar = _.get(
    state,
    "app_services.globalSearchBar.showSearchBar",
    false
  );

  const isGlobalSearchJumpToRevampEnabled = getIsGlobalSearchJumpToRevampEnabled();

  let cachedProjectGroups = [];

  if (!isGlobalSearchJumpToRevampEnabled) {
    cachedProjectGroups = getProjectGroupsFromCache({
      userInfo,
      curriculumProgramId: currentCurriculumProgramId,
      selectedAcademicYearId: currentAcademicYearId,
    });
  }

  const organizationId = _.get(userInfo, "org_id");
  const academicYearSelected = selectedAcademicYearSelector(state);

  const isHomeroomStudentsCountSkip = !getConfigValue({
    configuration: "Common:Homeroom",
    curriculumProgramId: currentCurriculumProgramId,
    userId,
    organizationId,
    identityId,
    role,
    userType: user_type,
  });

  const homeroomStudentsCountFilters = getHomeroomStudentsCountFilters({
    userId,
    academicYearSelected,
    currentCurriculumProgramId,
  });

  const loginActiveTab = _.get(state, "login.activeTab", "");

  const isData = _.get(ownProps, "isData", true);
  const isLoading = _.get(ownProps, "isLoading", false);

  // only call the query if the current curriculum program is MYP or DP
  const skipProjectGroupCategoriesQuery = !(
    user_type !== "parent" &&
    currentCurriculumProgramId &&
    currentCurriculumProgramType &&
    _.includes(
      [CURRICULUM_TYPE_MYP, CURRICULUM_TYPE_DP],
      currentCurriculumProgramType
    )
  );

  const projectGroupCategoriesQueryVariables = getProjectGroupCategoriesVariablesMemoized(
    {
      curriculumProgramIds: currentCurriculumProgramId
        ? [currentCurriculumProgramId]
        : [],
      organizationId,
      curriculumType: currentCurriculumProgramType,
    }
  );

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled({
    curriculumProgramId: currentCurriculumProgramId,
  });

  const isBlueprintCourseEnabled = isBlueprintTeacherCourseAssociationEnabled();

  return {
    userId,
    identityId,
    role,
    userType: user_type,
    currentCurriculumProgramId,
    curriculumNodeInterfaceType,
    isLearningCoursesEnabled,
    currentAcademicYearId,
    userEntityType,
    curriculumProgramIds,
    yearGroupCurriculumProgramFilter,
    portalType,
    projectGroupSubTypeFilters,
    showSearchBar,
    isGlobalSearchJumpToRevampEnabled,
    cachedProjectGroups,
    isHomeroomStudentsCountSkip,
    homeroomStudentsCountFilters,
    loginActiveTab,
    isUserInToddleLibrary: !_.isEmpty(
      _.get(state, ["platform", "sampleOrgPortalType"])
    ),
    organizationId,
    isData,
    isLoading,
    curriculumProgramId: currentCurriculumProgramId,
    skipProjectGroupCategoriesQuery,
    projectGroupCategoriesQueryVariables,
    isBlueprintCourseEnabled,
  };
};

const mapActionCreators = {
  setActiveFeatureWithKeyboardShortcuts,
  toggleGlobalSearchBarModal,
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapActionCreators),
  graphql(getUserProjectGroupsQuery, {
    name: "getUserProjectGroups",
    skip: ({
      userId,
      userEntityType,
      currentAcademicYearId,
      isGlobalSearchJumpToRevampEnabled,
    }) =>
      !userId ||
      !userEntityType ||
      !currentAcademicYearId ||
      !isGlobalSearchJumpToRevampEnabled,
    options: ({
      userId,
      userEntityType,
      curriculumProgramIds,
      yearGroupCurriculumProgramFilter,
      portalType,
      currentAcademicYearId,
      projectGroupSubTypeFilters,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
        filters: {
          curriculumProgramIds,
          projectGroupSubTypes: projectGroupSubTypeFilters,
          ...yearGroupCurriculumProgramFilter,
          yearGroupArchivalState: "ALL",
          academicYears: currentAcademicYearId ? [currentAcademicYearId] : [],
        },
        portalType,
      },
    }),
    props({
      getUserProjectGroups: { variables, networkStatus },
      ownProps: { isData, isLoading },
    }) {
      const data = getUserProjectGroupsFromCache(variables);

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        projectGroups: _.get(data, "node.projectGroups", []),
      };
    },
  }),
  graphql(getProjectGroupCategoriesQuery, {
    name: "getProjectGroupCategories",
    skip: ({ skipProjectGroupCategoriesQuery }) =>
      skipProjectGroupCategoriesQuery,
    options: ({ projectGroupCategoriesQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: projectGroupCategoriesQueryVariables,
    }),
    props({ getProjectGroupCategories, ownProps: { isData, isLoading } }) {
      const data = _.get(getProjectGroupCategories, "node", {});
      const networkStatus = _.get(getProjectGroupCategories, "networkStatus");

      const projectGroupCategories = _.get(data, "projectGroupCategories", []);

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        projectGroupCategories,
      };
    },
  }),
  withControlParameters({
    skip: ({ projectGroupCategories }) => _.isEmpty(projectGroupCategories),
    options: ({ projectGroupCategories }) => {
      const variables = getRnPQueryVariablesForProjectGroupCategories(
        projectGroupCategories
      );
      return {
        variables,
      };
    },
  }),
  graphql(getUserCoursesV2CountQuery, {
    name: "getUserCoursesV2Count",
    skip: ({ currentCurriculumProgramId, isLearningCoursesEnabled }) =>
      _.isEmpty(currentCurriculumProgramId) || isLearningCoursesEnabled,
    options: ({
      userId,
      currentCurriculumProgramId,
      currentAcademicYearId,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        userId: userId,
        courseFilters: {
          curriculumProgramIds: [currentCurriculumProgramId],
          academicYear: currentAcademicYearId,
          archivalState: "ALL",
          fetchOnlyMappedCourses: false,
        },
      },
    }),
    props({
      getUserCoursesV2Count: { variables, networkStatus },
      ownProps: { isData, isLoading },
    }) {
      const data = getUserCoursesV2CountFromCache(variables);

      const userCoursesV2TotalCount = _.get(data, "coursesV2.totalCount", 0);

      return {
        userCoursesCount: userCoursesV2TotalCount,
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getLearningCoursesCountQuery, {
    name: "getLearningCoursesCount",
    skip: ({ isLearningCoursesEnabled }) => !isLearningCoursesEnabled,
    options: ({ currentCurriculumProgramId, curriculumNodeInterfaceType }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        curriculumProgramId: currentCurriculumProgramId,
        curriculumType: curriculumNodeInterfaceType,
      },
    }),
    props({
      getLearningCoursesCount,
      ownProps: { isData, isLoading, isBlueprintCourseEnabled },
    }) {
      const { node, networkStatus } = getLearningCoursesCount;
      const learningCourse = _.get(node, "learningCourse", {});
      const countKey = isBlueprintCourseEnabled
        ? "totalCount"
        : "teacherCourseCount";
      const userLearningCoursesCount = _.get(
        learningCourse,
        `count.${countKey}`,
        0
      );

      return {
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        userLearningCoursesCount,
      };
    },
  }),
  graphql(getClassesCountQuery, {
    name: "getClassesCount",
    skip: ({ isLearningCoursesEnabled }) => !isLearningCoursesEnabled,
    options: ({ classesFeedQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: classesFeedQueryVariables,
    }),
    props({ getClassesCount, ownProps: { isData, isLoading } }) {
      const { node, networkStatus } = getClassesCount;
      const userCoursesCount = _.get(node, "courses.totalCount", 0);

      return {
        isData: !_.isEmpty(node) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        userCoursesCount,
      };
    },
  }),
  graphql(getHomeroomStudentsCountQuery, {
    name: "getHomeroomStudentsCount",
    skip: ({ isHomeroomStudentsCountSkip }) => isHomeroomStudentsCountSkip,
    options: ({ homeroomStudentsCountFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: homeroomStudentsCountFilters,
    }),
    props({
      getHomeroomStudentsCount: { variables, networkStatus },
      ownProps: { isData, isLoading },
    }) {
      const queryData = getHomeroomStudentsCountFromCache(variables);

      const homeroomStudentCount = _.get(
        queryData,
        "studentsRelation.totalCount",
        0
      );

      return {
        homeroomStudentCount,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        isData: !_.isEmpty(queryData) && isData,
      };
    },
  }),
  getEvaluationCyclesGraphql
)(JumpToSection);
